import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Search, Clock, Filter, ChevronDown, ChevronUp, X, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import SEO from '../components/SEO';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedComponents';
import { useSiteSettings } from '../context/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackagesPage = () => {
  const { settings } = useSiteSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [showAllDurations, setShowAllDurations] = useState(false);
  
  // Filter states - now arrays for multi-select
  const [filters, setFilters] = useState({
    region: searchParams.get('region')?.split(',').filter(Boolean) || [],
    theme: searchParams.get('theme')?.split(',').filter(Boolean) || [],
    duration: searchParams.get('duration')?.split(',').filter(Boolean) || []
  });

  const regions = [
    { id: 'south', name: 'South India' },
    { id: 'north', name: 'North India' },
    { id: 'east', name: 'East India' },
    { id: 'west', name: 'West India' }
  ];

  // All themes - order can be customized from admin
  const allThemes = [
    { id: 'beach', name: 'Beach' },
    { id: 'spiritual', name: 'Spiritual' },
    { id: 'cultural', name: 'Cultural & Heritage' },
    { id: 'hill_stations', name: 'Hill Stations' },
    { id: 'wildlife', name: 'Wildlife & Safari' },
    { id: 'ayurveda', name: 'Ayurveda & Wellness' },
    { id: 'adventure', name: 'Adventure' },
    { id: 'nature', name: 'Nature & Scenic' },
    { id: 'honeymoon', name: 'Honeymoon' }
  ];

  // Get featured themes order from settings (top 4 displayed by default)
  const featuredThemeIds = settings?.packages_page?.featured_themes || ['beach', 'spiritual', 'cultural', 'hill_stations'];
  
  // Sort themes: featured ones first in their order, then the rest
  const themes = [
    ...featuredThemeIds.map(id => allThemes.find(t => t.id === id)).filter(Boolean),
    ...allThemes.filter(t => !featuredThemeIds.includes(t.id))
  ];
  
  // Display themes: show only top 4 unless expanded
  const displayedThemes = showAllThemes ? themes : themes.slice(0, 4);

  const allDurations = [
    { id: '1-3', name: '1-3 Days' },
    { id: '4-6', name: '4-6 Days' },
    { id: '7-10', name: '7-10 Days' },
    { id: '10+', name: '10+ Days' }
  ];
  
  const displayedDurations = showAllDurations ? allDurations : allDurations.slice(0, 4);

  useEffect(() => {
    fetch(`${API_URL}/api/packages`)
      .then(res => res.json())
      .then(data => {
        setPackages(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Apply filters
  const filteredPackages = packages.filter(pkg => {
    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        pkg.title?.toLowerCase().includes(query) ||
        pkg.destination?.toLowerCase().includes(query) ||
        pkg.cities?.some(c => c.toLowerCase().includes(query)) ||
        pkg.state?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Region filter - multi-select (match if package has ANY of the selected regions)
    if (filters.region.length > 0) {
      const pkgRegions = Array.isArray(pkg.region) ? pkg.region : [pkg.region];
      const hasMatchingRegion = filters.region.some(r => pkgRegions.includes(r));
      if (!hasMatchingRegion) return false;
    }

    // Theme filter - multi-select (match if package has ANY of the selected themes)
    if (filters.theme.length > 0) {
      const pkgThemes = Array.isArray(pkg.theme) ? pkg.theme : [pkg.theme];
      const hasMatchingTheme = filters.theme.some(t => pkgThemes.includes(t));
      if (!hasMatchingTheme) return false;
    }

    // Duration filter - multi-select
    if (filters.duration.length > 0) {
      const nights = pkg.duration_nights || 0;
      const matchesDuration = filters.duration.some(d => {
        if (d === '1-3' && nights >= 1 && nights <= 3) return true;
        if (d === '4-6' && nights >= 4 && nights <= 6) return true;
        if (d === '7-10' && nights >= 7 && nights <= 10) return true;
        if (d === '10+' && nights >= 10) return true;
        return false;
      });
      if (!matchesDuration) return false;
    }

    return true;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    // Update URL params
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (filters.region.length) params.set('region', filters.region.join(','));
    if (filters.theme.length) params.set('theme', filters.theme.join(','));
    if (filters.duration.length) params.set('duration', filters.duration.join(','));
    setSearchParams(params);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const currentValues = prev[key] || [];
      const isSelected = currentValues.includes(value);
      return {
        ...prev,
        [key]: isSelected 
          ? currentValues.filter(v => v !== value)  // Remove if already selected
          : [...currentValues, value]               // Add if not selected
      };
    });
  };

  const clearFilters = () => {
    setFilters({ region: [], theme: [], duration: [] });
    setSearchQuery('');
    setSearchParams({});
  };

  const activeFilterCount = filters.region.length + filters.theme.length + filters.duration.length + (searchQuery ? 1 : 0);

  // Get breadcrumb text
  const getBreadcrumbText = () => {
    const parts = [];
    if (filters.region.length) {
      const regionNames = filters.region.map(r => regions.find(reg => reg.id === r)?.name || r);
      parts.push(regionNames.join(' + '));
    }
    if (filters.theme.length) {
      const themeNames = filters.theme.map(t => allThemes.find(th => th.id === t)?.name || t);
      parts.push(themeNames.join(' + '));
    }
    return parts.length > 0 ? parts.join(' • ') : 'All Packages';
  };

  return (
    <div className="min-h-screen bg-s-bg pt-16 md:pt-20">
      <SEO 
        title="India Tour Packages | Kerala, Rajasthan, Goa, Kashmir - Sentora Holidays" 
        description="Browse 50+ curated tour packages across India. Filter by South, North, East, West India. Beach holidays, spiritual tours, cultural heritage, hill stations, and Ayurveda retreats."
        keywords="India tour packages, Kerala tours, Rajasthan packages, Goa holidays, Kashmir trip, hill station tours, beach holidays, spiritual tours, Ayurveda packages"
        url="https://www.sentoraholidays.com/packages"
      />

      {/* Search Header */}
      <section className="bg-gradient-to-r from-s-primary/10 via-s-bg2 to-s-primary/10 border-b border-s-border py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <AnimatedSection className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-s-text font-heritage mb-2">Discover Your Perfect Journey</h1>
            <p className="text-s-text2 text-sm md:text-base">Explore 50+ handcrafted itineraries across incredible India</p>
          </AnimatedSection>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="bg-s-card rounded-2xl shadow-xl p-3 flex items-center border border-s-border hover:border-s-primary/30 transition-colors">
              <div className="flex-1 flex items-center gap-3 px-3">
                <div className="w-10 h-10 rounded-full bg-s-primary/10 flex items-center justify-center">
                  <MapPin className="text-s-primary" size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-s-primary font-medium">Where would you like to go?</p>
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="text-s-text font-medium outline-none w-full text-base bg-transparent placeholder:text-s-muted"
                    placeholder="Try Goa, Kerala, Rajasthan..."
                    data-testid="packages-search-input"
                  />
                </div>
              </div>
              <Button type="submit" className="bg-gradient-to-r from-s-primary to-s-primary hover:from-s-primary/90 hover:to-s-primary/90 rounded-xl h-12 w-12 md:h-12 md:w-36 p-0 shadow-lg">
                <Search className="text-white md:mr-2" size={18} />
                <span className="hidden md:inline text-white font-medium">Search</span>
              </Button>
            </div>
          </form>

          {/* Results Header */}
          <div className="flex items-center justify-between mt-6">
            <div>
              <p className="text-s-text2 text-sm">
                <span className="text-s-text font-semibold">{filteredPackages.length}</span> {filteredPackages.length === 1 ? 'tour' : 'tours'} found
                {getBreadcrumbText() !== 'All Packages' && <span className="ml-1">in {getBreadcrumbText()}</span>}
              </p>
            </div>
            
            {/* Mobile Filter Button */}
            <Button
              variant="outline"
              className="lg:hidden border-s-border"
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter size={18} className="mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-s-primary text-white">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar - Filters (Desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-s-text">Filters</h2>
                {activeFilterCount > 0 && (
                  <button onClick={clearFilters} className="text-sm text-s-primary hover:underline">
                    Clear all
                  </button>
                )}
              </div>

              {/* Region Filter */}
              <div className="space-y-3">
                <h3 className="font-medium text-s-text text-sm">Region</h3>
                {regions.map(region => (
                  <label key={region.id} className="flex items-center gap-3 cursor-pointer group">
                    <Checkbox
                      checked={filters.region.includes(region.id)}
                      onCheckedChange={() => updateFilter('region', region.id)}
                    />
                    <span className={`text-sm ${filters.region.includes(region.id) ? 'text-s-text font-medium' : 'text-s-text2'}`}>
                      {region.name}
                    </span>
                  </label>
                ))}
              </div>

              {/* Theme Filter */}
              <div className="space-y-3">
                <h3 className="font-medium text-s-text text-sm">Theme</h3>
                {displayedThemes.map(theme => (
                  <label key={theme.id} className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={filters.theme.includes(theme.id)}
                      onCheckedChange={() => updateFilter('theme', theme.id)}
                    />
                    <span className={`text-sm ${filters.theme.includes(theme.id) ? 'text-s-text font-medium' : 'text-s-text2'}`}>
                      {theme.name}
                    </span>
                  </label>
                ))}
                {themes.length > 4 && (
                  <button
                    onClick={() => setShowAllThemes(!showAllThemes)}
                    className="flex items-center gap-1 text-sm text-s-primary hover:underline mt-2"
                  >
                    {showAllThemes ? (
                      <>
                        <ChevronUp size={14} /> Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={14} /> View All ({themes.length - 4} more)
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Duration Filter */}
              <div className="space-y-3">
                <h3 className="font-medium text-s-text text-sm">Duration</h3>
                {displayedDurations.map(dur => (
                  <label key={dur.id} className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={filters.duration.includes(dur.id)}
                      onCheckedChange={() => updateFilter('duration', dur.id)}
                    />
                    <span className={`text-sm ${filters.duration.includes(dur.id) ? 'text-s-text font-medium' : 'text-s-text2'}`}>
                      {dur.name}
                    </span>
                  </label>
                ))}
                {allDurations.length > 4 && (
                  <button
                    onClick={() => setShowAllDurations(!showAllDurations)}
                    className="flex items-center gap-1 text-sm text-s-primary hover:underline mt-2"
                  >
                    {showAllDurations ? (
                      <>
                        <ChevronUp size={14} /> Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={14} /> View All ({allDurations.length - 4} more)
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* Mobile Filters Modal */}
          {showMobileFilters && (
            <div className="fixed inset-0 bg-black/50 z-50 lg:hidden">
              <div className="absolute bottom-0 left-0 right-0 bg-s-bg rounded-t-3xl max-h-[80vh] overflow-y-auto">
                <div className="sticky top-0 bg-s-bg p-4 border-b border-s-border flex items-center justify-between">
                  <h2 className="font-bold text-s-text">Filters</h2>
                  <button onClick={() => setShowMobileFilters(false)}>
                    <X size={24} className="text-s-text" />
                  </button>
                </div>
                <div className="p-4 space-y-6">
                  {/* Region */}
                  <div className="space-y-3">
                    <h3 className="font-medium text-s-text">Region</h3>
                    <div className="flex flex-wrap gap-2">
                      {regions.map(region => (
                        <Badge
                          key={region.id}
                          className={`cursor-pointer ${filters.region.includes(region.id) ? 'bg-s-primary text-white' : 'bg-s-bg2 text-s-text2'}`}
                          onClick={() => updateFilter('region', region.id)}
                        >
                          {region.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {/* Theme */}
                  <div className="space-y-3">
                    <h3 className="font-medium text-s-text">Theme</h3>
                    <div className="flex flex-wrap gap-2">
                      {(showAllThemes ? themes : themes.slice(0, 4)).map(theme => (
                        <Badge
                          key={theme.id}
                          className={`cursor-pointer ${filters.theme.includes(theme.id) ? 'bg-s-primary text-white' : 'bg-s-bg2 text-s-text2'}`}
                          onClick={() => updateFilter('theme', theme.id)}
                        >
                          {theme.name}
                        </Badge>
                      ))}
                    </div>
                    {themes.length > 4 && (
                      <button
                        onClick={() => setShowAllThemes(!showAllThemes)}
                        className="flex items-center gap-1 text-sm text-s-primary mt-1"
                      >
                        {showAllThemes ? (
                          <><ChevronUp size={14} /> Show Less</>
                        ) : (
                          <><ChevronDown size={14} /> View All ({themes.length - 4} more)</>
                        )}
                      </button>
                    )}
                  </div>
                  {/* Duration */}
                  <div className="space-y-3">
                    <h3 className="font-medium text-s-text">Duration</h3>
                    <div className="flex flex-wrap gap-2">
                      {allDurations.map(dur => (
                        <Badge
                          key={dur.id}
                          className={`cursor-pointer ${filters.duration.includes(dur.id) ? 'bg-s-primary text-white' : 'bg-s-bg2 text-s-text2'}`}
                          onClick={() => updateFilter('duration', dur.id)}
                        >
                          {dur.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="sticky bottom-0 bg-s-bg p-4 border-t border-s-border flex gap-4">
                  <Button variant="outline" className="flex-1" onClick={clearFilters}>
                    Clear All
                  </Button>
                  <Button className="flex-1 bg-s-primary" onClick={() => setShowMobileFilters(false)}>
                    Show {filteredPackages.length} Results
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Package Cards Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-s-primary"></div>
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="text-center py-20">
                <h3 className="text-xl font-bold text-s-text mb-2">No packages found</h3>
                <p className="text-s-text2 mb-4">Try adjusting your filters or search query</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredPackages.map((pkg, idx) => (
                  <AnimatedCard
                    key={pkg.id}
                    delay={idx * 50}
                    hoverEffect="lift-subtle"
                    className="group"
                  >
                    <Link
                      to={`/packages/${pkg.id}`}
                      className="block h-full"
                      data-testid={`package-card-${pkg.id}`}
                    >
                    <Card className="bg-s-card border-s-border overflow-hidden hover:shadow-xl transition-all h-full">
                      {/* Image */}
                      <div className="relative h-48">
                        <img
                          src={pkg.image}
                          alt={pkg.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Theme Tags - support multiple */}
                        <div className="absolute top-3 left-3 flex flex-wrap gap-1 max-w-[80%]">
                          {(Array.isArray(pkg.theme) ? pkg.theme : [pkg.theme]).slice(0, 2).map((t, i) => (
                            <Badge key={i} className="bg-s-primary text-white text-[10px]">
                              {t?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Tour'}
                            </Badge>
                          ))}
                          {Array.isArray(pkg.theme) && pkg.theme.length > 2 && (
                            <Badge className="bg-s-primary text-white text-[10px]">+{pkg.theme.length - 2}</Badge>
                          )}
                        </div>
                        {/* Duration */}
                        <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-sm font-bold">
                          {pkg.duration_nights ?? 3}N / {pkg.duration_days ?? 4}D
                        </div>
                      </div>

                      {/* Content */}
                      <CardContent className="p-4">
                        {/* Title */}
                        <h3 className="text-s-text font-bold text-lg mb-2 line-clamp-2 group-hover:text-s-primary transition-colors">
                          {pkg.title}
                        </h3>

                        {/* Cities */}
                        <p className="text-s-text2 text-sm mb-3 line-clamp-1 flex items-center gap-1">
                          <MapPin size={14} />
                          {pkg.cities?.join(' • ') || pkg.destination}
                        </p>

                        {/* Bottom Row */}
                        <div className="flex items-center justify-between">
                          {/* Duration Badge */}
                          <div className="flex items-center gap-1 text-s-text2 text-sm">
                            <Clock size={14} />
                            {pkg.duration_days ?? 4} Days / {pkg.duration_nights ?? 3} Nights
                          </div>

                          {/* View Link */}
                          <span className="text-s-primary text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                            View <ArrowRight size={14} />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  </AnimatedCard>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackagesPage;
